import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

/**
 * Route prefixes for the data-entry steps of the planner and notification flows.
 * While the user is on one of these, they may have unsaved progress (e.g. a
 * partially complete notification), so leaving the app risks losing it. The
 * completion/confirmation, welcome and auth pages are deliberately excluded.
 */
const FLOW_ROUTE_PREFIXES = [
  '/notify/deceased',
  '/notify/notifier',
  '/notify/services',
  '/notify/documents',
  '/notify/review',
  '/notify/update/',
  '/planner/details',
  '/planner/services',
  '/planner/documents',
  '/planner/trusted-person',
  '/planner/review',
  '/planner/update/',
];

/**
 * Tracks whether the user is part-way through a flow that holds unsaved work, and
 * installs a browser `beforeunload` safety net so a refresh / tab-close / external
 * link is warned about. In-app link clicks are handled separately (BrochureService).
 */
@Injectable({ providedIn: 'root' })
export class UnsavedWorkService {

  private currentUrl = '';
  private leaving = false;

  constructor(private router: Router) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.urlAfterRedirects;
      }
    });

    window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
      if (this.isFlowInProgress && !this.leaving) {
        // Triggers the browser's native "Leave site? Changes may not be saved" prompt.
        event.preventDefault();
        event.returnValue = '';
      }
    });
  }

  get isFlowInProgress(): boolean {
    const url = this.currentUrl.split('?')[0];
    return FLOW_ROUTE_PREFIXES.some(prefix => url.startsWith(prefix));
  }

  /**
   * Suppress the beforeunload prompt for a navigation the user has already confirmed
   * (e.g. via the in-app "Leave" dialog), so they aren't asked to confirm twice.
   */
  permitNavigation(): void {
    this.leaving = true;
  }
}
